'use client';

import { useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import { SLoadingContainer } from './SLoadingContainer';

/**
 * 전역 로딩 오버레이 싱글톤 — sd-loading-container 의 `sdLoading.show()/hide()` 포팅.
 * 앱 어디서든 `loading.show({ message })` / `loading.hide()` 로 호출한다.
 * 최초 show 시 document.body 에 오버레이 루트를 자동 마운트한다.
 */
interface LoadingState {
  open: boolean;
  message?: string;
}

let state: LoadingState = { open: false, message: undefined };
const listeners = new Set<() => void>();

const emit = () => listeners.forEach(l => l());
const setState = (patch: Partial<LoadingState>) => {
  state = { ...state, ...patch };
  emit();
};
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const getSnapshot = () => state;

/** 오버레이 뷰포트 — 스토어를 구독해 SLoadingContainer 를 렌더한다. 직접 배치도 가능. */
export function SLoadingViewport() {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return <SLoadingContainer open={s.open} message={s.message} />;
}

let mounted = false;
function ensureMounted() {
  if (mounted || typeof document === 'undefined') return;
  mounted = true;
  const host = document.createElement('div');
  host.setAttribute('data-sd-loading-root', '');
  document.body.appendChild(host);
  createRoot(host).render(<SLoadingViewport />);
}

export const loading = {
  /** 로딩 오버레이 표시 (메시지 갱신도 동일 호출) */
  show(options: { message?: string } = {}) {
    ensureMounted();
    setState({ open: true, message: options.message });
  },
  /** 로딩 오버레이 숨김 */
  hide() {
    setState({ open: false });
  },
};
